/**
 * Phase 44 Batch BF1 — SpecialAssessmentsField.
 *
 * Repeatable card-array editor for multi-year condo special
 * assessments. Lives below the HOA field in the wizard's expense
 * step. Each row captures: name, monthly_amount, payments_remaining,
 * outstanding_balance (optional — auto-derived if blank),
 * pay_off_at_closing toggle.
 *
 * The user's canonical motivating example (NW 68th Ave Apt F203,
 * Hialeah FL): 1 row with $283.59/mo × 149 mo, balance $42,254.91,
 * pay-off-at-closing OFF — flips cap rate from 6.79% (inflated) to
 * 5.05% (honest).
 *
 * Limit: 10 rows per deal (matches the backend ``DealInputs`` cap;
 * in practice >3 simultaneous assessments is rare).
 */
import { Plus, Trash2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { NumberField } from "@/pages/wizard/_shared";

const MAX_ROWS = 10;

function blankAssessment() {
  return {
    // Phase 46 Batch D — stable React key. Generated at row creation
    // so the array's React identity tracks the row, not its index.
    // Reordering / removing rows above this one no longer recycles
    // controlled-input state into a neighbouring row.
    _key:
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `sa-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    name: "",
    monthly_amount: 0,
    payments_remaining: 0,
    outstanding_balance: null,
    pay_off_at_closing: false,
  };
}

export default function SpecialAssessmentsField({ value, onChange }) {
  // `value` is always an array (default []). Guarded for legacy drafts
  // that pre-date the field.
  const rows = Array.isArray(value) ? value : [];

  function updateRow(idx, patch) {
    onChange(rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }
  function removeRow(idx) {
    onChange(rows.filter((_, i) => i !== idx));
  }
  function addRow() {
    if (rows.length >= MAX_ROWS) return;
    onChange([...rows, blankAssessment()]);
  }

  return (
    <div className="col-span-full mt-2" data-testid="special-assessments-field">
      <div className="mb-2 flex items-baseline justify-between">
        <label className="text-sm font-medium text-slate-700">
          Special assessments{" "}
          <span className="text-xs font-normal text-slate-500">
            (multi-year HOA assessments — common on FL / Surfside-affected condos)
          </span>
        </label>
        {rows.length === 0 ? (
          <button
            type="button"
            onClick={addRow}
            data-testid="special-assessments-add-empty"
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
          >
            <Plus className="h-3.5 w-3.5" />
            Add special assessment
          </button>
        ) : null}
      </div>

      {rows.length === 0 ? (
        <p className="text-xs text-slate-500">
          None. If you don't know whether the property carries one, request
          the estoppel letter — Florida condos commonly have $200–500/mo
          assessments stacked on top of regular HOA.
        </p>
      ) : (
        <div className="space-y-3" data-testid="special-assessments-list">
          {rows.map((row, idx) => {
            // Auto-derive outstanding_balance preview when the field is empty.
            const derivedBalance =
              (Number(row.monthly_amount) || 0) *
              (Number(row.payments_remaining) || 0);
            return (
              <div
                // Phase 46 Batch D — stable `_key` set on row creation.
                // Legacy drafts without `_key` fall back to a
                // composite of position + name so the field doesn't
                // break for in-flight drafts.
                key={row._key || `legacy-${idx}-${row.name || ""}`}
                data-testid={`special-assessment-row-${idx}`}
                className="rounded-lg border border-slate-200 bg-slate-50/40 p-4"
              >
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Assessment {idx + 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeRow(idx)}
                    data-testid={`special-assessment-remove-${idx}`}
                    className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-rose-700 hover:bg-rose-50"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Remove
                  </button>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <label
                      htmlFor={`sa-name-${idx}`}
                      className="mb-1 block text-xs font-medium text-slate-600"
                    >
                      Name <span className="text-slate-400">(optional)</span>
                    </label>
                    <input
                      id={`sa-name-${idx}`}
                      type="text"
                      value={row.name || ""}
                      onChange={(e) => updateRow(idx, { name: e.target.value })}
                      placeholder="e.g. Concrete restoration 2024-2038"
                      data-testid={`special-assessment-name-${idx}`}
                      className="block w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>

                  <NumberField
                    id={`sa-monthly-${idx}`}
                    label="Monthly amount"
                    prefix="$"
                    suffix="/mo"
                    value={row.monthly_amount}
                    onChange={(v) => updateRow(idx, { monthly_amount: v })}
                    testid={`special-assessment-monthly-${idx}`}
                  />

                  <NumberField
                    id={`sa-remaining-${idx}`}
                    label="Payments remaining"
                    suffix="months"
                    value={row.payments_remaining}
                    onChange={(v) =>
                      updateRow(idx, { payments_remaining: Math.max(0, Math.round(v || 0)) })
                    }
                    testid={`special-assessment-remaining-${idx}`}
                  />

                  <NumberField
                    id={`sa-balance-${idx}`}
                    label="Outstanding balance"
                    prefix="$"
                    value={
                      row.outstanding_balance === null ||
                      row.outstanding_balance === undefined ||
                      row.outstanding_balance === ""
                        ? ""
                        : row.outstanding_balance
                    }
                    onChange={(v) =>
                      updateRow(idx, { outstanding_balance: v === 0 ? null : v })
                    }
                    testid={`special-assessment-balance-${idx}`}
                    hint={
                      !row.outstanding_balance && derivedBalance > 0
                        ? `Auto: $${derivedBalance.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}`
                        : "Leave blank to auto-derive"
                    }
                  />

                  <div className="flex items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2">
                    <div>
                      <p className="text-xs font-medium text-slate-700">
                        Pay off at closing?
                      </p>
                      <p className="text-[11px] text-slate-500">
                        Settle the balance upfront; no monthly stream.
                      </p>
                    </div>
                    <Switch
                      checked={Boolean(row.pay_off_at_closing)}
                      onCheckedChange={(checked) =>
                        updateRow(idx, { pay_off_at_closing: !!checked })
                      }
                      data-testid={`special-assessment-pay-off-${idx}`}
                    />
                  </div>
                </div>
              </div>
            );
          })}
          {rows.length < MAX_ROWS ? (
            <button
              type="button"
              onClick={addRow}
              data-testid="special-assessments-add-another"
              className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:border-slate-400 hover:text-slate-800"
            >
              <Plus className="h-3.5 w-3.5" />
              Add another assessment
            </button>
          ) : (
            <p className="text-[11px] text-slate-500">
              Maximum of {MAX_ROWS} assessments per deal.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
